export const IMPERSONATION_STARTER_ROLE = 'orderprotection_engineering' as const;

/**
 * Prefix for the opaque impersonation credential (`op_imp_<lookupId>_<secret>`). Mirrors the PAT model: the cookie
 * carries no target claims, only a random lookup id plus a secret the server verifies against a peppered HMAC.
 */
export const IMPERSONATION_CREDENTIAL_PREFIX = 'op_imp' as const;

/** Name of the HttpOnly cookie carrying the credential. Shared so the writer and reader cannot drift apart. */
export const IMPERSONATION_COOKIE_NAME = 'op_impersonation' as const;

/** Marker stamped on every credentialed request; the only impersonation signal visible on a CORS preflight. */
export const IMPERSONATION_REQUEST_HEADER = 'x-op-impersonation' as const;

/** Absolute session lifetime in minutes. Must stay within the 60-minute cap the database CHECK enforces. */
export const IMPERSONATION_SESSION_TTL_MINUTES = 30;

/** Control routes that must stay reachable while impersonating, or the engineer cannot exit. */
export const IMPERSONATION_CONTROL_ROUTE_SEGMENT = '/auth/impersonation/' as const;

/** Roles eligible when impersonating an existing user. */
export const IMPERSONATION_USER_TARGET_ROLES = [
  'account_owner',
  'account_admin',
  'account_support',
  'account_user',
  'developer',
] as const;

/** Role-mode personas with account-wide merchant store access. */
export const IMPERSONATION_ACCOUNT_WIDE_TARGET_ROLES = ['account_owner', 'account_admin', 'account_support'] as const;

/** Role-mode personas that require one selected merchant store. */
export const IMPERSONATION_STORE_SCOPED_TARGET_ROLES = ['account_user'] as const;

/** Store roles accepted with a store-scoped merchant persona. */
export const IMPERSONATION_MERCHANT_STORE_TARGET_ROLES = [
  'store_owner',
  'store_admin',
  'store_support',
  'store_user',
] as const;

/** Developer role-mode is scoped to an organization and, optionally, one approved collaboration. */
export const IMPERSONATION_DEVELOPER_TARGET_ROLES = ['developer'] as const;
export const IMPERSONATION_DEVELOPER_STORE_TARGET_ROLES = ['store_collaborator'] as const;

export const IMPERSONATION_MODE = {
  USER: 'User',
  ROLE: 'Role',
} as const;

export type ImpersonationMode = (typeof IMPERSONATION_MODE)[keyof typeof IMPERSONATION_MODE];

export const IMPERSONATION_SESSION_STATE = {
  ACTIVE: 'Active',
  ENDED: 'Ended',
  EXPIRED: 'Expired',
  REVOKED: 'Revoked',
} as const;

export type ImpersonationSessionState = (typeof IMPERSONATION_SESSION_STATE)[keyof typeof IMPERSONATION_SESSION_STATE];

export const IMPERSONATION_AUDIT_EVENT_TYPE = {
  STARTED: 'Started',
  STOPPED: 'Stopped',
  EXPIRED: 'Expired',
  REVOKED: 'Revoked',
  DENIED: 'Denied',
} as const;

export type ImpersonationAuditEventType =
  (typeof IMPERSONATION_AUDIT_EVENT_TYPE)[keyof typeof IMPERSONATION_AUDIT_EVENT_TYPE];

export const IMPERSONATION_TERMINAL_SOURCE = {
  END: 'End',
  LOGOUT: 'Logout',
  ABSOLUTE_EXPIRY: 'AbsoluteExpiry',
  ACTOR_INVALIDATION: 'ActorInvalidation',
  TARGET_INVALIDATION: 'TargetInvalidation',
  ROLLOUT_DISABLEMENT: 'RolloutDisablement',
  ADMINISTRATIVE_REVOCATION: 'AdministrativeRevocation',
  UNRECOVERABLE_ERROR: 'UnrecoverableError',
} as const;

export type ImpersonationTerminalSource =
  (typeof IMPERSONATION_TERMINAL_SOURCE)[keyof typeof IMPERSONATION_TERMINAL_SOURCE];

/** Stable codes clients may branch on without receiving sensitive denial details. */
export const IMPERSONATION_CLIENT_ERROR = {
  DENIED: 'IMPERSONATION_DENIED',
  FEATURE_DISABLED: 'IMPERSONATION_FEATURE_DISABLED',
  INVALID_TARGET: 'IMPERSONATION_INVALID_TARGET',
  ACTIVE_SESSION_EXISTS: 'IMPERSONATION_ACTIVE_SESSION_EXISTS',
  SESSION_NOT_FOUND: 'IMPERSONATION_SESSION_NOT_FOUND',
  SESSION_TERMINAL: 'IMPERSONATION_SESSION_TERMINAL',
  REAUTHENTICATION_REQUIRED: 'IMPERSONATION_REAUTHENTICATION_REQUIRED',
  RECOVERY_REQUIRED: 'IMPERSONATION_RECOVERY_REQUIRED',
} as const;

export type ImpersonationClientError = (typeof IMPERSONATION_CLIENT_ERROR)[keyof typeof IMPERSONATION_CLIENT_ERROR];

/**
 * Direct grants that identify an identity record as OP-internal and therefore
 * ineligible for user-mode impersonation.
 *
 * Keep this explicit instead of deriving it from role grants: eligibility must
 * fail closed even when a reserved entitlement is granted directly to a
 * customer-role user. The shared-auth drift test binds this list to every
 * approved OP-only entitlement group.
 */
export const IMPERSONATION_RESERVED_ENTITLEMENTS: ReadonlySet<string> = new Set([
  'admin:re-install',
  'admin:cli-use',
  'admin-stores:create',
  'admin-stores:delete',
  'admin-stores:read',
  'admin-stores:update',
  'admin-users:create',
  'admin-users:delete',
  'admin-users:read',
  'admin-users:update',
  'claim-automation:read',
  'claim-automation:update',
  'credentials:read',
  'credentials:update',
  'finance-admin:read',
  'finance-admin:update',
  'implementation:read',
  'implementation:deploy',
  'implementation:review',
  'implementation:approve',
  'implementation:reject',
  'implementation:create',
  'implementation:brand-create',
  'engineering:impersonate',
  'engineering:read',
  'engineering:update',
  'slack:create-channel',
  'slack:view-channel',
  'support:member-create',
  'support:member-delete',
  'support:member-read',
  'support:member-update',
  'support-user:create',
  'whitelabel:list',
  'whitelabel:restart',
  'app-review:read',
  'app-review:approve',
  'app-review:reject',
  'app-review:suspend',
  'store:all-read',
]);
